import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ChevronDown, ChevronRight, ListChecks, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatIST } from '../../lib/store';

const ALL = 'all';
const PAGE_SIZE = 50;

const STATUS_STYLE = {
  sent: 'bg-emerald-50 text-emerald-700',
  delivered: 'bg-emerald-50 text-emerald-700',
  read: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-700',
  skipped: 'bg-amber-50 text-amber-700',
};

export function DeliveryBadge({ status }) {
  if (!status) return null;
  return (
    <Badge variant="outline" className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-none border-transparent ${STATUS_STYLE[status] || 'bg-heda-cream text-heda-mute'}`}>
      {status}
    </Badge>
  );
}

/** "+91 98450 12345" for a stored 91XXXXXXXXXX number. */
export function showPhone(p) {
  const s = String(p || '');
  return /^91\d{10}$/.test(s) ? `+91 ${s.slice(2, 7)} ${s.slice(7)}` : s;
}

/**
 * Admin → WhatsApp → Delivery log. Every outbound WhatsApp attempt
 * (reminders, digests, test sends), newest first, with what DoubleTick
 * answered. Times shown in IST.
 */
export default function WhatsAppDeliveryLog({ people = [] }) {
  const [filters, setFilters] = useState({ user_id: ALL, message_type: ALL, status: ALL, date_from: '', date_to: '' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openRow, setOpenRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== ALL) params[k] = v; });
      const res = await api.getWhatsappDeliveryLog(params);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load the delivery log');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const types = data?.message_types || {};
  const rows = data?.rows || [];

  return (
    <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="whatsapp-delivery-log">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-heda-gold" />
            <h3 className="font-display text-[15px] font-bold text-heda-ink">Delivery log</h3>
            {data && <span className="text-[12px] text-heda-mute">{data.total} message{data.total === 1 ? '' : 's'} · kept {data.retention_days} days</span>}
          </div>
          <Button className="h-9 text-[12.5px] rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" size="sm" variant="outline" onClick={load} data-testid="delivery-log-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Select value={filters.user_id} onValueChange={(v) => setFilter('user_id', v)}>
            <SelectTrigger className="rounded-xl text-sm bg-heda-cream/50" data-testid="delivery-filter-person"><SelectValue placeholder="Person" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Everyone</SelectItem>
              {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.message_type} onValueChange={(v) => setFilter('message_type', v)}>
            <SelectTrigger className="rounded-xl text-sm bg-heda-cream/50" data-testid="delivery-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All types</SelectItem>
              {Object.entries(types).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilter('status', v)}>
            <SelectTrigger className="rounded-xl text-sm bg-heda-cream/50" data-testid="delivery-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All results</SelectItem>
              {(data?.statuses || ['sent', 'failed', 'skipped', 'delivered', 'read']).map((s) => (
                <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilter('date_from', e.target.value)} className="rounded-xl text-sm bg-heda-cream/50" aria-label="From date (IST)" data-testid="delivery-filter-from" />
          <Input type="date" value={filters.date_to} onChange={(e) => setFilter('date_to', e.target.value)} className="rounded-xl text-sm bg-heda-cream/50" aria-label="To date (IST)" data-testid="delivery-filter-to" />
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center gap-2 text-sm text-heda-mute py-8"><Loader2 className="w-4 h-4 animate-spin text-heda-gold" /> Loading…</div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-heda-mute py-6 text-center">No messages match these filters.</p>
        ) : (
          <div className="border border-heda-line rounded-xl overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="heda-thead">
                <tr>
                  <th className="px-3 py-2.5 font-semibold">Time (IST)</th>
                  <th className="px-3 py-2.5 font-semibold">Person</th>
                  <th className="px-3 py-2.5 font-semibold hidden md:table-cell">Type</th>
                  <th className="px-3 py-2.5 font-semibold hidden lg:table-cell">Template</th>
                  <th className="px-3 py-2.5 font-semibold">Result</th>
                  <th className="px-3 py-2.5 font-semibold hidden md:table-cell">Reason</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const open = openRow === r.id;
                  return (
                    <React.Fragment key={r.id}>
                      <tr className="border-t border-heda-line align-top cursor-pointer hover:bg-heda-cream/40" onClick={() => setOpenRow(open ? null : r.id)} data-testid={`delivery-row-${r.id}`}>
                        <td className="px-3 py-2.5 whitespace-nowrap tabular-nums text-heda-sub">
                          {open ? <ChevronDown className="inline w-3.5 h-3.5 mr-1" /> : <ChevronRight className="inline w-3.5 h-3.5 mr-1" />}
                          {formatIST(r.sent_at)}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-heda-ink">{r.recipient_name || '—'}</div>
                          <div className="text-[11px] text-heda-mute tabular-nums">{showPhone(r.recipient_phone) || '—'}</div>
                        </td>
                        <td className="px-3 py-2.5 hidden md:table-cell text-heda-sub">{types[r.message_type] || r.message_type}</td>
                        <td className="px-3 py-2.5 hidden lg:table-cell font-mono text-[11.5px] text-heda-sub">{r.template_name}{r.via === 'text_fallback' ? ' (fallback)' : ''}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <div className="flex flex-col items-start gap-1">
                            <DeliveryBadge status={r.status} />
                            {r.delivery_status && <DeliveryBadge status={r.delivery_status} />}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 hidden md:table-cell text-[12px] text-heda-sub max-w-xs break-words">
                          {r.failure_reason || r.delivery_error || (r.http_status ? `HTTP ${r.http_status}` : '')}
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-heda-cream/40">
                          <td colSpan={6} className="px-3 py-3 text-[12px] space-y-2">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <p><span className="heda-label !inline">Type</span> {types[r.message_type] || r.message_type}</p>
                              <p><span className="heda-label !inline">Template</span> <span className="font-mono">{r.template_name}</span></p>
                              <p><span className="heda-label !inline">HTTP</span> {r.http_status ?? '— (not sent)'}</p>
                              <p className="break-all"><span className="heda-label !inline">Message id</span> <span className="font-mono">{r.provider_message_id || '—'}</span></p>
                              {r.delivery_updated_at && <p><span className="heda-label !inline">Receipt</span> {r.delivery_status} at {formatIST(r.delivery_updated_at)}</p>}
                              {r.failure_reason && <p className="md:col-span-2 text-red-700 break-words">{r.failure_reason}</p>}
                            </div>
                            <p className="heda-label">Request</p>
                            <pre className="whitespace-pre-wrap break-all rounded-xl border border-heda-line bg-white p-2 font-mono text-[11px] max-h-48 overflow-auto">{JSON.stringify(r.request || {}, null, 2)}</pre>
                            <p className="heda-label">DoubleTick response</p>
                            <pre className="whitespace-pre-wrap break-all rounded-xl border border-heda-line bg-white p-2 font-mono text-[11px] max-h-48 overflow-auto">{r.api_response || '—'}</pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data && data.pages > 1 && (
          <div className="flex items-center justify-between text-[12.5px] text-heda-sub">
            <span>Page {data.page} of {data.pages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-9 rounded-full border-heda-line bg-white shadow-none" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Newer</Button>
              <Button size="sm" variant="outline" className="h-9 rounded-full border-heda-line bg-white shadow-none" disabled={page >= data.pages || loading} onClick={() => setPage((p) => p + 1)}>Older</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
